import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { Box, Button, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ThumbsUp } from 'react-feather';
import { TActionExecution } from 'src/modules/assistant/types/message';

const MAX_TIMEOUT_MS = 2147483647;
const BUTTON_ICON_SIZE = 15;
const TIME_FORMAT = 'LT';
const DATE_TIME_FORMAT = 'L LT';

interface DraftResultProps {
    isAICall: boolean;
    recorded: boolean;
    completedAt: Moment | null;
    nextFollowUpAt: Moment | null;
    execution: TActionExecution | null;
    liked: boolean;
    onLike: () => void;
    onContinue: () => void;
}

/**
 * Enables "continue follow-up" once the next follow-up date is reached.
 */
const useFollowUpReady = (nextFollowUpAt: Moment | null) => {
    const [ready, setReady] = useState(
        !nextFollowUpAt || moment().isSameOrAfter(nextFollowUpAt)
    );

    useEffect(() => {
        const remaining = nextFollowUpAt ? nextFollowUpAt.diff(moment()) : 0;
        if (remaining <= 0) {
            setReady(true);
            return undefined;
        }
        setReady(false);
        const timer = window.setTimeout(
            () => setReady(true),
            Math.min(remaining, MAX_TIMEOUT_MS)
        );
        return () => window.clearTimeout(timer);
    }, [nextFollowUpAt]);

    return ready;
};

export const DraftResult = ({
    isAICall,
    recorded,
    completedAt,
    nextFollowUpAt,
    execution,
    liked,
    onLike,
    onContinue,
}: DraftResultProps) => {
    const { t } = useTranslation();
    const followUpReady = useFollowUpReady(nextFollowUpAt);

    return (
        <>
            <Alert severity="success">
                {isAICall
                    ? t('Assistant.AICallCompleted')
                    : t('Assistant.ActionCompleted')}{' '}
                ·{' '}
                {t('Assistant.ActionCompletedAt', {
                    time: (completedAt || moment()).format(TIME_FORMAT),
                })}
            </Alert>
            {!recorded && (
                <Alert severity="warning">
                    {t('Assistant.ActionSaveError')}
                </Alert>
            )}
            {isAICall && execution?.summary && (
                <Typography variant="body2">
                    <strong>{t('Assistant.AICallSummary')}</strong>{' '}
                    {execution.summary}
                </Typography>
            )}
            {nextFollowUpAt && (
                <Typography variant="body2">
                    {t('Assistant.NextFollowUpAt', {
                        date: nextFollowUpAt.format(DATE_TIME_FORMAT),
                    })}
                </Typography>
            )}
            <Box display="flex" flexWrap="wrap" gridGap={7}>
                <Button
                    aria-pressed={liked}
                    disabled={liked}
                    onClick={onLike}
                    size="small"
                    startIcon={
                        <ThumbsUp aria-hidden="true" size={BUTTON_ICON_SIZE} />
                    }
                    variant="outlined"
                >
                    {liked
                        ? t('Assistant.SuggestionLiked')
                        : t('Assistant.LikeSuggestion')}
                </Button>
                <Button
                    color="primary"
                    disabled={!followUpReady}
                    endIcon={
                        <ArrowRight
                            aria-hidden="true"
                            size={BUTTON_ICON_SIZE}
                        />
                    }
                    onClick={onContinue}
                    size="small"
                    variant="contained"
                >
                    {t('Assistant.ContinueFollowUp')}
                </Button>
            </Box>
        </>
    );
};
